import { Navigate, useLocation } from 'react-router-dom';
import { builderQuestionSearch, type RetiredReport } from './retiredReports.js';

/**
 * Sends a retired report path to the composed question that replaced it,
 * keeping the reader's scope.
 *
 * THE READER'S QUERY STRING WINS ON SCOPE AND LOSES ON THE QUESTION, and the
 * split is the whole of this component. `?from=&to=&worksiteId=` is what the
 * reader chose and is why they bookmarked the link at all — dropping it would
 * land them on a different period and quietly change the answer they were
 * looking at. `measure=`/`plus=`/`groupBy=` describe which report this is, and
 * the old page had no such parameters, so anything of that shape in an incoming
 * URL is stale rather than intended.
 *
 * `replace` keeps the dead path out of history: Back should return to wherever
 * they came from, not bounce through the redirect again. Same contract
 * `RedirectToWorkspace` already honours for the retired workspace lenses.
 */
export function RedirectToBuilder({ report }: { report: RetiredReport }): JSX.Element {
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // The question, layered over whatever scope arrived. `set` rather than a
  // merge: a link carrying a `groupBy` from some earlier generation of this
  // path must not survive into a question this table did not compose.
  for (const [key, value] of new URLSearchParams(builderQuestionSearch(report))) {
    params.set(key, value);
  }
  /* `thenBy` and `shape` are deleted rather than overwritten, because the
     question composed here declares neither — and a leftover `thenBy=` would be
     checked against a measure that does not declare the pair, turning a
     redirect into a refusal for a clause the reader never asked for. */
  params.delete('thenBy');
  params.delete('shape');

  /* `/reports` — the smart report, which is now the front door. It reads the
     SAME URL keys the builder does (`useBuilderQuestion` is one hook serving
     both), so a retired report arrives as a composed question on the screen
     that replaced the composer, with no second encoding of what the report
     means. */
  return <Navigate to={{ pathname: '/reports', search: `?${params}` }} replace />;
}

export default RedirectToBuilder;
